import React, { useState, useEffect, useCallback } from 'react';
import { Container, Box, Typography, Paper, IconButton, Button, Alert } from '@mui/material';
import SummaryTab from './requestingSummaryTab';
import TransferBondLayout from '../tbondlayout';
import { useLocation, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import { toast } from 'react-toastify';
import QuotesSection from './QuotesSection';
import EditTransferBondModal from './editTransferBondModal';
import CancelRequestModal from './cancelRequestModal'; // Import the new CancelRequestModal
import RequestingPowers from './requestingPowers';

const ViewDetails = () => {
  const [bondDetails, setBondDetails] = useState(null);
  const [transactionStatus, setTransactionStatus] = useState('Pending'); 
  const location = useLocation();
  const navigate = useNavigate(); 
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openCancelModal, setOpenCancelModal] = useState(false); // State for cancel modal
  const [canViewPostingAgency, setCanViewPostingAgency] = useState(false);

  const params = new URLSearchParams(location.search);
  const id = params.get('ID');

  useEffect(() => {
    // Scrolls the window to the top when the component is mounted
    window.scrollTo(0, 0);
  }, []);

  const fetchBondDetails = useCallback(async () => {
    if (!id) return;
  
    const token = localStorage.getItem('jwtToken');
    const baseURL = process.env.REACT_APP_BASE_URL;
  
    try {
      // Fetch Bond Details
      const bondDetailsResponse = await fetch(`${baseURL}RequestingTransferBond/${id}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
  
      if (!bondDetailsResponse.ok) {
        throw new Error(`Error fetching bond details: ${bondDetailsResponse.statusText}`);
      }
  
      const bondData = await bondDetailsResponse.json();
      setBondDetails(bondData);
      setTransactionStatus(bondData.statusDescription || 'Unknown');
  
      // Fetch Permissions
      const userDetailsResponse = await fetch(`${baseURL}user/info`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
  
      if (!userDetailsResponse.ok) {
        throw new Error(`Error fetching user details: ${userDetailsResponse.statusText}`);
      }
  
      const userDetails = await userDetailsResponse.json();
      const { subAgentId, agencyId } = userDetails;
  
      const permissionsResponse = await fetch(
        `${baseURL}User/${agencyId}/permissions/${subAgentId}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
  
      if (!permissionsResponse.ok) {
        throw new Error(`Error fetching user permissions: ${permissionsResponse.statusText}`);
      }
  
      const permissions = await permissionsResponse.json();
      const hasPermission = permissions.some(
        (perm) => perm.portalPermissionTypeID === 9 && perm.permit === true
      );
      setCanViewPostingAgency(hasPermission); // Update state based on permission
    } catch (error) {
      toast.error('Error fetching bond details or permissions.');
      console.error('Error:', error);
    }
  }, [id]);

  useEffect(() => {
    fetchBondDetails(); 

    const intervalId = setInterval(() => {
      fetchBondDetails();
    }, 10000); 

    return () => clearInterval(intervalId);
  }, [fetchBondDetails]);

  const handleBack = () => {
    navigate('/request-transfer-bond-dashboard');
  };

  const handleOpenEditModal = () => {
    setOpenEditModal(true);
  };

  const handleCloseEditModal = () => {
    setOpenEditModal(false);
  };

  const handleOpenCancelModal = () => {
    setOpenCancelModal(true); // Open the cancel modal
  };

  const handleCloseCancelModal = () => {
    setOpenCancelModal(false); // Close the cancel modal
  };

  const handleRefresh = () => {
    fetchBondDetails();
  };

  return (
    <TransferBondLayout>
      <Container maxWidth="lg" sx={{ paddingTop: 4 }}>
      <Box sx={{ padding: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={handleBack} color="primary">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" gutterBottom sx={{ ml: 1 }}>
            Requesting Dashboard
          </Typography>
        </Box>

        <Paper
          sx={{
            padding: 2,
            mb: 2,
            borderRadius: 1,
            backgroundColor: '#f9f9f9',
            boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.1)',
            textAlign: 'left',
            border: '1px solid #e0e0e0',
          }}
        >
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
            In this section, you can:
          </Typography>
          <Box sx={{ lineHeight: 1.8 }}>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', fontSize: '1rem' }}>
              <CheckCircleIcon sx={{ fontSize: 18, mr: 1, color: 'primary.main' }} /> View and select quotes for the transfer bond request. You'll be able to provide additional details to the posting agent when you select a quote.
            </Typography>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', fontSize: '1rem' }}>
              <CheckCircleIcon sx={{ fontSize: 18, mr: 1, color: 'primary.main' }} /> Modify or cancel the request (prior to accepting a quote).
            </Typography>
          </Box>

          {(bondDetails?.executingAgentID === null && bondDetails?.isDeferred === "N") && (
            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button variant="contained" color="primary" onClick={handleOpenEditModal}>
                Edit this Request
              </Button>
              <Button variant="contained" color="secondary" onClick={handleOpenCancelModal}>
                Cancel this Request
              </Button>
            </Box>
          )}
        </Paper>

        {/* Payment Required Up Front Alert */}
        {bondDetails?.acceptedQuoteID && bondDetails?.quotes?.find(q => q.transferBondQuoteID === bondDetails.acceptedQuoteID)?.paymentRequiredUpFront && (
          <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 3, mb: 2 }}>
            <Typography variant="body1" fontWeight="bold">
              Payment Required
            </Typography>
            <Typography variant="body2">
              In order to proceed with this transfer bond, please reach out to a Bankers associate to process the payment.
            </Typography>
          </Alert>
        )}

        <Box sx={{ mt: 4 }}>
          <QuotesSection bondDetails={bondDetails} refreshHandler={handleRefresh} canViewPostingAgency={canViewPostingAgency} />
        </Box>

        <Box sx={{ mt: 4 }}>
          <RequestingPowers powers={bondDetails?.powers || []} />
        </Box>

        <Box sx={{ mt: 4 }}>
          <SummaryTab bondDetails={bondDetails} canViewPostingAgency={canViewPostingAgency} />
        </Box>
      </Box>
      </Container>

      <EditTransferBondModal open={openEditModal} onClose={handleCloseEditModal} refreshHandler={handleRefresh} bondDetails={bondDetails} />
      <CancelRequestModal open={openCancelModal} onClose={handleCloseCancelModal} bondDetails={bondDetails} refreshHandler={handleRefresh} /> 
    </TransferBondLayout>
  );
};

export default ViewDetails;
